/* ============================================================
   VITALCASA CRM — ModalWhatsAppApiPlantilla
   Envío de plantillas oficiales de WhatsApp Business API.

   Props:
   - isOpen:   boolean
   - onClose:  () => void
   - contact:  { nombre, telefono, iniciales }
   - agente:   string  (nombre del agente, para pre-rellenar)
   ============================================================ */

import { useState, useRef, useEffect } from 'react';
import { useTheme } from './ThemeProvider';

const WA_GREEN       = '#25d366';
const WA_GREEN_DARK  = '#128c7e';
const WA_GREEN_FROM  = '#075e54';
const WA_SHADOW      = 'rgba(37,211,102,0.35)';
const WA_BG          = 'rgba(37,211,102,0.08)';
const WA_BORDER      = 'rgba(37,211,102,0.22)';

// ── Definición de plantillas (espejo del backend) ──────────
const PLANTILLAS = [
  {
    id: 'confirmar_cita',
    grupo: 'Cita',
    label: '📅 Confirmar cita',
    vars: ['nombre', 'fecha', 'hora', 'agente'],
  },
  { id: 'visita_reservado', grupo: 'Visita', label: '🟢 Reservado', vars: ['nombre'] },
  {
    id: 'visita_sigue_1_vivienda',
    grupo: 'Visita',
    label: '🟡 Sigue interesado · 1 vivienda',
    vars: ['nombre', 'tipo', 'calle'],
  },
  {
    id: 'visita_sigue_varias_viviendas',
    grupo: 'Visita',
    label: '🟡 Sigue interesado · varias',
    vars: ['nombre', 'num_viviendas'],
  },
  {
    id: 'visita_objeciones_1_vivienda',
    grupo: 'Visita',
    label: '🟠 Objeciones · 1 vivienda',
    vars: ['nombre', 'tipo', 'calle'],
  },
  {
    id: 'visita_objeciones_varias_viviendas',
    grupo: 'Visita',
    label: '🟠 Objeciones · varias',
    vars: ['nombre', 'num_viviendas'],
  },
  {
    id: 'visita_descarte_1_vivienda',
    grupo: 'Visita',
    label: '🔴 Descarte · 1 vivienda',
    vars: ['nombre', 'tipo', 'calle'],
  },
  {
    id: 'visita_descarte_varias_viviendas',
    grupo: 'Visita',
    label: '🔴 Descarte · varias',
    vars: ['nombre', 'num_viviendas'],
  },
  { id: 'visita_descarte', grupo: 'Visita', label: '🔴 Descarte (legado)', vars: ['nombre'] },
];

const VAR_LABELS = {
  nombre:        'Nombre del cliente',
  fecha:         'Fecha de la visita',
  hora:          'Hora de la visita',
  agente:        'Nombre del agente',
  tipo:          'Tipo de vivienda (ej: piso, chalet)',
  calle:         'Calle / ubicación',
  num_viviendas: 'Nº de viviendas (ej: 2)',
};

export default function ModalWhatsAppApiPlantilla({ isOpen, onClose, contact, agente }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';

  const [plantillaId, setPlantillaId] = useState(PLANTILLAS[0].id);
  const [vars, setVars]                 = useState({});
  const [enviando, setEnviando]         = useState(false);
  const [resultado, setResultado]       = useState(null); // null | 'ok' | 'error'
  const [errorMsg, setErrorMsg]         = useState('');

  // Drag
  const [pos, setPos]       = useState(null);
  const dragRef             = useRef(null);
  const dragOffsetRef       = useRef(null);

  const startDrag = (e) => {
    if (e.target.closest('button, select, input')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => {
      dragOffsetRef.current = null;
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Pre-rellenar variables cuando cambia el contexto
  useEffect(() => {
    if (!isOpen) return;
    setResultado(null);
    setErrorMsg('');
    setPlantillaId(PLANTILLAS[0].id);
    setVars({
      nombre: contact?.nombre || '',
      agente: agente || '',
    });
  }, [isOpen, contact?.nombre, agente]);

  if (!isOpen) return null;

  const plantillaActual = PLANTILLAS.find((p) => p.id === plantillaId) || PLANTILLAS[0];

  const varsFaltantes = plantillaActual.vars.filter(v => !String(vars[v] || '').trim());
  const puedeEnviar   = varsFaltantes.length === 0 && contact?.telefono;

  const handleEnviar = async () => {
    if (!puedeEnviar || enviando) return;
    setEnviando(true);
    try {
      const res = await fetch('/api/whatsapp/api-plantillas/enviar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          telefono:     contact.telefono,
          plantilla_id: plantillaActual.id,
          variables:    vars,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setResultado('ok');
        setTimeout(() => { setResultado(null); onClose?.(); }, 2000);
      } else {
        setResultado('error');
        setErrorMsg(data.error || 'Error desconocido');
      }
    } catch (err) {
      setResultado('error');
      setErrorMsg(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const setVar = (key, value) => setVars(prev => ({ ...prev, [key]: value }));

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 500, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? `0 0 60px 15px ${WA_SHADOW}, 0 0 120px 40px rgba(37,211,102,0.08)`
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>

        {/* Confirmación enviado */}
        {resultado === 'ok' && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 2,
          }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: WA_GREEN }}>Mensaje enviado</div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500 }}>
              Plantilla enviada a {contact?.nombre}
            </div>
          </div>
        )}

        {/* Barra verde superior */}
        <div style={{
          height: 3, flexShrink: 0,
          background: `linear-gradient(90deg, ${WA_GREEN}, ${WA_GREEN_DARK}, ${WA_GREEN_FROM}, ${WA_GREEN_DARK}, ${WA_GREEN})`,
        }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{
          padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, cursor: 'grab', userSelect: 'none',
          background: isDark ? 'transparent' : '#e0e0d8',
          borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${WA_GREEN_FROM}, ${WA_GREEN_DARK})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, boxShadow: `0 4px 12px ${WA_SHADOW}`,
            }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="#fff">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Plantilla WhatsApp</h2>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, fontWeight: 500, color: c.textMuted }}>
                {contact?.nombre || 'Cliente'} · {contact?.telefono || '—'}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Cuerpo scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: isDark ? 0 : '4px 0 8px' }}>

          {/* Selector de plantilla (todas) */}
          <div style={{ margin: '12px 12px 0', padding: '14px 16px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 12, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${WA_GREEN}`, boxShadow: isDark ? 'none' : '0 3px 10px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: isDark ? c.textMuted : WA_GREEN_DARK, marginBottom: 8 }}>
              Elige plantilla
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {PLANTILLAS.map((p, idx) => (
                <div key={p.id}>
                  {(idx === 0 || PLANTILLAS[idx - 1].grupo !== p.grupo) && (
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: c.textMuted,
                      margin: idx === 0 ? '0 0 4px' : '8px 0 4px',
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>
                      {p.grupo}
                    </div>
                  )}
                  <button type="button" onClick={() => setPlantillaId(p.id)} style={{
                    width: '100%', padding: '8px 12px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 12, fontWeight: 600, textAlign: 'left',
                    border: `1.5px solid ${plantillaId === p.id ? WA_GREEN : WA_BORDER}`,
                    background: plantillaId === p.id ? WA_BG : 'transparent',
                    color: plantillaId === p.id ? WA_GREEN : c.textSecondary,
                  }}>{p.label}</button>
                </div>
              ))}
            </div>
          </div>

          {/* Variables */}
          <div style={{ margin: '10px 12px 0', padding: '14px 16px', background: isDark ? 'transparent' : '#ffffff', borderRadius: 12, border: isDark ? 'none' : '1px solid #c8c8be', borderLeft: isDark ? 'none' : `4px solid ${WA_GREEN}`, boxShadow: isDark ? 'none' : '0 3px 10px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: isDark ? c.textMuted : WA_GREEN_DARK, marginBottom: 10 }}>
              Datos del mensaje · {plantillaActual.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {plantillaActual.vars.map(v => (
                <div key={v}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: c.textMuted, display: 'block', marginBottom: 3 }}>
                    {VAR_LABELS[v] || v}
                    {!String(vars[v] || '').trim() && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
                  </label>
                  <input
                    value={vars[v] || ''}
                    onChange={e => setVar(v, e.target.value)}
                    placeholder={VAR_LABELS[v] || v}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: 8,
                      border: `1.5px solid ${!String(vars[v] || '').trim() ? 'rgba(239,68,68,0.4)' : (isDark ? c.border : '#a8a8a0')}`,
                      background: isDark ? c.bgInput : 'rgba(37,211,102,0.06)',
                      color: c.textPrimary, fontSize: 13, fontFamily: 'inherit',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Error */}
          {resultado === 'error' && (
            <div style={{
              margin: '10px 12px 0', padding: '12px 14px', borderRadius: 10,
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#ef4444', fontSize: 12, fontWeight: 600,
            }}>
              ⚠️ {errorMsg}
            </div>
          )}

          {/* Aviso sin teléfono */}
          {!contact?.telefono && (
            <div style={{
              margin: '10px 12px 0', padding: '10px 14px', borderRadius: 10,
              background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)',
              color: '#ca8a04', fontSize: 12, fontWeight: 600,
            }}>
              ⚠️ Este contacto no tiene número de teléfono registrado
            </div>
          )}

          <div style={{ height: 8 }} />
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px 18px',
          display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
          flexShrink: 0, borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          background: isDark ? 'transparent' : '#e0e0d8',
        }}>
          <button onClick={onClose} style={{
            padding: '11px 24px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
            background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary,
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
          }}>Cancelar</button>
          <button onClick={handleEnviar} disabled={!puedeEnviar || enviando} style={{
            padding: '11px 30px', borderRadius: 10, border: 'none',
            background: puedeEnviar && !enviando
              ? `linear-gradient(135deg, ${WA_GREEN_FROM}, ${WA_GREEN_DARK})`
              : c.bgInput,
            color: puedeEnviar && !enviando ? '#fff' : c.textMuted,
            cursor: puedeEnviar && !enviando ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
            boxShadow: puedeEnviar && !enviando ? `0 4px 14px ${WA_SHADOW}` : 'none',
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'all 0.15s',
          }}>
            {enviando ? (
              <>
                <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                Enviando…
              </>
            ) : (
              <>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
                Enviar plantilla
              </>
            )}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
